/**
 * The client half of the API's tag rules, in one place.
 *
 * These lived inside EventTagField, which said in a comment that they would
 * move here the moment a second caller needed them. Bulk tagging is that
 * caller: it decides "does this event already carry the tag" for a hundred
 * events at once, and if it answered that question differently from the field
 * the two would disagree about what a duplicate is — the bulk path would write
 * "Sport" onto an event already carrying "sport", and the API would then
 * collapse them and report a change nobody can see.
 */

/**
 * Trim and collapse internal whitespace — the client half of `normalise_tag`.
 */
export const normaliseTag = (value: string): string =>
	value.split(/\s+/).filter(Boolean).join(" ");

/** The API's matching rule: same tag ignoring case, accents kept distinct. */
export const sameTag = (a: string, b: string): boolean =>
	a.localeCompare(b, "en-AU", { sensitivity: "accent" }) === 0;

/**
 * `existing` plus whichever of `adding` it does not already carry.
 *
 * Returns the ORIGINAL array when nothing is new, so a caller can use identity
 * to tell "this event needs writing" from "this event is already filed", and
 * skip the request. Bulk tagging leans on that: picking a tag that most of the
 * selection already has should write the few that lack it, not all hundred.
 *
 * The event's own spelling wins over the one being added, matching the field's
 * behaviour — the tag is already on the document and re-spelling it here would
 * be an edit nobody asked for.
 */
export function mergeTags(existing: string[], adding: string[]): string[] {
	const next = [...existing];
	for (const raw of adding) {
		const tag = normaliseTag(raw);
		if (!tag) continue;
		if (next.some((have) => sameTag(have, tag))) continue;
		next.push(tag);
	}
	return next.length === existing.length ? existing : next;
}
